import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  getExplorerLookDeeperScenario,
  upsertExplorerLookDeeperScenario,
  type LookDeeperScenario,
  type UpsertScenarioPayload,
} from '../lib/explorerLookDeeperService';

interface ExplorerLookDeeperEditorProps {
  songId: string;
  onUpdate: () => void;
  onDirtyStateChange?: (isDirty: boolean) => void;
}

export interface ExplorerLookDeeperEditorHandle {
  save: () => Promise<boolean>;
  hasUnsavedChanges: () => boolean;
}

interface EditorState {
  setup_text: string;
  choice_a_label: string;
  choice_a_outcome: string;
  choice_b_label: string;
  choice_b_outcome: string;
  most_people_miss: string;
  internal_source_notes: string;
}

const ExplorerLookDeeperEditor = forwardRef<ExplorerLookDeeperEditorHandle, ExplorerLookDeeperEditorProps>(
  ({ songId, onUpdate, onDirtyStateChange }, ref) => {
    const [loading, setLoading] = useState(true);

    const [state, setState] = useState<EditorState>({
      setup_text: '',
      choice_a_label: '',
      choice_a_outcome: '',
      choice_b_label: '',
      choice_b_outcome: '',
      most_people_miss: '',
      internal_source_notes: '',
    });

    const [initialState, setInitialState] = useState<EditorState>({
      setup_text: '',
      choice_a_label: '',
      choice_a_outcome: '',
      choice_b_label: '',
      choice_b_outcome: '',
      most_people_miss: '',
      internal_source_notes: '',
    });

  useEffect(() => {
    loadScenario();
  }, [songId]);

  useEffect(() => {
    const isDirty = JSON.stringify(state) !== JSON.stringify(initialState);
    if (onDirtyStateChange) {
      onDirtyStateChange(isDirty);
    }
  }, [state, initialState, onDirtyStateChange]);

  async function loadScenario() {
    setLoading(true);
    const { data, error } = await getExplorerLookDeeperScenario(songId);

    if (error) {
      console.error('Error loading Look Deeper scenario:', error);
      setLoading(false);
      return;
    }

    if (data) {
      const { scenario } = data;

      const loadedState: EditorState = {
        setup_text: scenario.setup_text || '',
        choice_a_label: scenario.choice_a_label || '',
        choice_a_outcome: scenario.choice_a_outcome || '',
        choice_b_label: scenario.choice_b_label || '',
        choice_b_outcome: scenario.choice_b_outcome || '',
        most_people_miss: scenario.most_people_miss || '',
        internal_source_notes: scenario.internal_source_notes || '',
      };

      setState(loadedState);
      setInitialState(loadedState);
    }

    setLoading(false);
  }

  function updateField<K extends keyof EditorState>(field: K, value: EditorState[K]) {
    setState(prev => ({ ...prev, [field]: value }));
  }

  const isDirty = JSON.stringify(state) !== JSON.stringify(initialState);

  async function handleSave(): Promise<boolean> {
    try {
      const payload: UpsertScenarioPayload = {
        song_id: songId,
        setup_text: state.setup_text,
        choice_a_label: state.choice_a_label,
        choice_a_outcome: state.choice_a_outcome,
        choice_b_label: state.choice_b_label,
        choice_b_outcome: state.choice_b_outcome,
        most_people_miss: state.most_people_miss,
        internal_source_notes: state.internal_source_notes || null,
        lens_override: null,
      };

      const { data, error } = await upsertExplorerLookDeeperScenario(payload);

      if (error) {
        console.error('Error saving Look Deeper scenario:', error);
        return false;
      }

      if (data) {
        await loadScenario();
        onUpdate();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error saving Look Deeper scenario:', error);
      return false;
    }
  }

  useImperativeHandle(ref, () => ({
    save: handleSave,
    hasUnsavedChanges: () => isDirty,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[#A0A0A0]">Loading scenario...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Setup Text */}
      <div>
        <label className="block text-[13px] font-medium text-[#E0E0E0] mb-2">
          Scenario
        </label>
        <textarea
          value={state.setup_text}
          onChange={(e) => updateField('setup_text', e.target.value)}
          placeholder="Describe the situation..."
          rows={6}
          className="w-full px-4 py-3 bg-[#111111] border border-[#282828] rounded-lg text-[#F5F5F5] placeholder-[#707070] focus:outline-none focus:border-[#40E0D0]/50 resize-none"
        />
      </div>

      {/* Choice A */}
      <div className="space-y-3 p-4 bg-[#1A1A1A] border border-[#282828] rounded-lg">
        <div className="text-[14px] font-medium text-[#F5F5F5]">Choice A</div>
        <div>
          <label className="block text-[13px] text-[#A0A0A0] mb-2">
            Decision
          </label>
          <input
            type="text"
            value={state.choice_a_label}
            onChange={(e) => updateField('choice_a_label', e.target.value)}
            placeholder="e.g., Share what you know publicly"
            className="w-full px-4 py-2 bg-[#111111] border border-[#282828] rounded-lg text-[#F5F5F5] placeholder-[#707070] focus:outline-none focus:border-[#40E0D0]/50"
          />
        </div>
        <div>
          <label className="block text-[13px] text-[#A0A0A0] mb-2">
            Outcome
          </label>
          <textarea
            value={state.choice_a_outcome}
            onChange={(e) => updateField('choice_a_outcome', e.target.value)}
            placeholder="What happens if they choose this..."
            rows={4}
            className="w-full px-4 py-3 bg-[#111111] border border-[#282828] rounded-lg text-[#F5F5F5] placeholder-[#707070] focus:outline-none focus:border-[#40E0D0]/50 resize-none"
          />
        </div>
      </div>

      {/* Choice B */}
      <div className="space-y-3 p-4 bg-[#1A1A1A] border border-[#282828] rounded-lg">
        <div className="text-[14px] font-medium text-[#F5F5F5]">Choice B</div>
        <div>
          <label className="block text-[13px] text-[#A0A0A0] mb-2">
            Decision
          </label>
          <input
            type="text"
            value={state.choice_b_label}
            onChange={(e) => updateField('choice_b_label', e.target.value)}
            placeholder="e.g., Work through existing relationships"
            className="w-full px-4 py-2 bg-[#111111] border border-[#282828] rounded-lg text-[#F5F5F5] placeholder-[#707070] focus:outline-none focus:border-[#40E0D0]/50"
          />
        </div>
        <div>
          <label className="block text-[13px] text-[#A0A0A0] mb-2">
            Outcome
          </label>
          <textarea
            value={state.choice_b_outcome}
            onChange={(e) => updateField('choice_b_outcome', e.target.value)}
            placeholder="What happens if they choose this..."
            rows={4}
            className="w-full px-4 py-3 bg-[#111111] border border-[#282828] rounded-lg text-[#F5F5F5] placeholder-[#707070] focus:outline-none focus:border-[#40E0D0]/50 resize-none"
          />
        </div>
      </div>

      {/* What Most People Miss */}
      <div>
        <label className="block text-[13px] font-medium text-[#E0E0E0] mb-2">
          What Most People Miss
        </label>
        <textarea
          value={state.most_people_miss}
          onChange={(e) => updateField('most_people_miss', e.target.value)}
          placeholder="The key insight about systems or patterns..."
          rows={3}
          className="w-full px-4 py-3 bg-[#111111] border border-[#282828] rounded-lg text-[#F5F5F5] placeholder-[#707070] focus:outline-none focus:border-[#40E0D0]/50 resize-none"
        />
      </div>

      {/* Internal Source Notes */}
      <div>
        <label className="block text-[13px] font-medium text-[#E0E0E0] mb-2">
          Internal Source Notes (admin only, optional)
        </label>
        <textarea
          value={state.internal_source_notes}
          onChange={(e) => updateField('internal_source_notes', e.target.value)}
          placeholder="Citations, references, or notes for other admins..."
          rows={3}
          className="w-full px-4 py-3 bg-[#111111] border border-[#282828] rounded-lg text-[#F5F5F5] placeholder-[#707070] focus:outline-none focus:border-[#40E0D0]/50 resize-none"
        />
      </div>
    </div>
  );
});

ExplorerLookDeeperEditor.displayName = 'ExplorerLookDeeperEditor';

export default ExplorerLookDeeperEditor;
